import React, { useState } from 'react';
import { Plus, Filter, Search } from 'lucide-react';
import { DndContext, DragEndEvent, DragOverlay, useSensor, useSensors, PointerSensor, TouchSensor } from '@dnd-kit/core';
import { Layout } from '../components/layout/Layout';
import { DealCard } from '../components/kanban/DealCard';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Modal } from '../components/ui/Modal';
import { Select } from '../components/ui/Select';
import { useToast } from '../components/ui/Toast';
import { deals as initialDeals, sellers } from '../utils/mockData';
import { Deal } from '../types';
const COLUMNS = [{
  id: 'novo',
  title: 'Novo Lead',
  color: 'border-blue-500'
}, {
  id: 'negociacao',
  title: 'Em Negociação',
  color: 'border-yellow-500'
}, {
  id: 'proposta',
  title: 'Proposta Enviada',
  color: 'border-purple-500'
}, {
  id: 'pagamento',
  title: 'Aguardando Pagamento',
  color: 'border-orange-500'
}, {
  id: 'fechado',
  title: 'Fechado ✅',
  color: 'border-emerald-500'
}, {
  id: 'perdido',
  title: 'Perdido ❌',
  color: 'border-red-500'
}];
export function Kanban() {
  const {
    addToast
  } = useToast();
  const [deals, setDeals] = useState<Deal[]>(initialDeals);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeId, setActiveId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState('novo');
  const [isLoading, setIsLoading] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5
    }
  }), useSensor(TouchSensor));
  const handleDragStart = (event: any) => {
    setActiveId(event.active.id);
  };
  const handleDragEnd = (event: DragEndEvent) => {
    const {
      active,
      over
    } = event;
    if (over && active.id !== over.id) {
      // Find which column we dropped into
      // Note: In a real dnd-kit implementation with SortableContext,
      // we would need more complex logic to find the container.
      // For this demo, we'll assume dropping on a column container or a card in that column
      // Simplified logic for demo:
      // In a full implementation, we'd use collision detection strategies
      // Here we'll just simulate the update
      const dealId = active.id as string;
      // For demo purposes, we'll just log it and show a toast,
      // since fully implementing dnd-kit sortable lists requires more boilerplate
      // than fits in this single file update easily.
      // However, we can simulate the "drop" effect by checking if we dropped on a column ID
      const overId = over.id as string;
      const isColumn = COLUMNS.some(col => col.id === overId);
      if (isColumn) {
        setDeals(prev => prev.map(deal => deal.id === dealId ? {
          ...deal,
          stage: overId as any
        } : deal));
        const stageName = COLUMNS.find(c => c.id === overId)?.title;
        addToast(`Negócio movido para ${stageName}`, 'success');
      }
    }
    setActiveId(null);
  };
  const handleAddDeal = (columnId?: string) => {
    if (columnId) setSelectedColumn(columnId);
    setIsModalOpen(true);
  };
  const handleSaveDeal = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      setIsModalOpen(false);
      addToast('Negócio adicionado com sucesso!', 'success');
      // In a real app, we'd add the deal to the state here
    }, 1000);
  };
  const filteredDeals = deals.filter(deal => deal.storeName.toLowerCase().includes(searchTerm.toLowerCase()) || deal.contactName.toLowerCase().includes(searchTerm.toLowerCase()));
  const getDealsByStage = (stage: string) => {
    return filteredDeals.filter(deal => deal.stage === stage);
  };
  const activeDeal = activeId ? deals.find(d => d.id === activeId) : null;
  return <Layout data-id="element-539">
      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd} data-id="element-540">
        <div className="h-[calc(100vh-120px)] flex flex-col" data-id="element-541">
          {/* Header */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6" data-id="element-542">
            <div data-id="element-543">
              <h1 className="text-3xl font-bold text-white" data-id="element-544">
                Pipeline de Vendas
              </h1>
              <p className="text-text-muted" data-id="element-545">
                Gerencie seus negócios e arraste para atualizar o status.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto" data-id="element-546">
              <div className="w-full md:w-64" data-id="element-547">
                <Input placeholder="Buscar negócio..." icon={<Search className="h-4 w-4" data-id="element-549" />} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-548" />
              </div>
              <Button variant="outline" icon={<Filter className="h-4 w-4" data-id="element-551" />} data-id="element-550">
                Filtros
              </Button>
              <Button variant="primary" icon={<Plus className="h-4 w-4" data-id="element-553" />} onClick={() => handleAddDeal('novo')} data-id="element-552">
                Novo
              </Button>
            </div>
          </div>

          {/* Board Area */}
          <div className="flex-1 overflow-x-auto pb-4" data-id="element-554">
            <div className="flex gap-4 min-w-[1200px] h-full" data-id="element-555">
              {COLUMNS.map(column => {
              const columnDeals = getDealsByStage(column.id);
              const totalValue = columnDeals.reduce((acc, deal) => acc + deal.estimatedValue, 0);
              return <div key={column.id} id={column.id} // Important for drop detection
              className="flex-1 min-w-[280px] flex flex-col h-full" data-id="element-556">
                    {/* Column Header */}
                    <div className={`bg-card p-3 rounded-t-xl border-t-4 ${column.color} mb-2`} data-id="element-557">
                      <div className="flex justify-between items-center mb-1" data-id="element-558">
                        <h3 className="font-bold text-white text-sm" data-id="element-559">
                          {column.title}
                        </h3>
                        <span className="bg-slate-700 text-xs px-2 py-0.5 rounded-full text-text-secondary" data-id="element-560">
                          {columnDeals.length}
                        </span>
                      </div>
                      <p className="text-xs text-text-muted font-medium" data-id="element-561">
                        {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(totalValue)}
                      </p>
                    </div>

                    {/* Drop Zone */}
                    <div className="flex-1 bg-slate-900/50 rounded-xl p-2 overflow-y-auto custom-scrollbar border border-white/5" data-id="element-562">
                      {columnDeals.map(deal => <div key={deal.id} className="mb-3" data-id="element-563">
                          <DealCard deal={deal} data-id="element-564" />
                        </div>)}

                      <button onClick={() => handleAddDeal(column.id)} className="w-full py-3 border border-dashed border-slate-700 rounded-lg text-text-muted hover:text-white hover:border-slate-500 hover:bg-slate-800/50 transition-all text-sm flex items-center justify-center gap-2 mt-2" data-id="element-565">
                        <Plus className="h-4 w-4" data-id="element-566" />
                        Adicionar
                      </button>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        <DragOverlay data-id="element-567">
          {activeDeal ? <div className="transform rotate-3 cursor-grabbing" data-id="element-568">
              <DealCard deal={activeDeal} data-id="element-569" />
            </div> : null}
        </DragOverlay>

        {/* Add Deal Modal */}
        <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title="Adicionar Negócio" footer={<>
              <Button variant="ghost" onClick={() => setIsModalOpen(false)} data-id="element-571">
                Cancelar
              </Button>
              <Button variant="primary" onClick={handleSaveDeal} isLoading={isLoading} data-id="element-572">
                Salvar
              </Button>
            </>} data-id="element-570">
          <form className="space-y-4" data-id="element-573">
            <Input label="Loja/Cliente" placeholder="Buscar cliente..." data-id="element-574" />
            <div className="grid grid-cols-2 gap-4" data-id="element-575">
              <Input label="Valor Estimado (R$)" placeholder="0,00" type="number" data-id="element-576" />
              <Select label="Etapa" value={selectedColumn} onChange={e => setSelectedColumn(e.target.value)} options={COLUMNS.map(c => ({
              value: c.id,
              label: c.title
            }))} data-id="element-577" />
            </div>
            <Select label="Vendedor Responsável" options={sellers.map(s => ({
            value: s.id,
            label: s.name
          }))} data-id="element-578" />
            <Input label="Observações" placeholder="Detalhes..." data-id="element-579" />
          </form>
        </Modal>
      </DndContext>
    </Layout>;
}